import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CaretDownOutlined,
  CheckOutlined,
  CloseOutlined,
  CloudSyncOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SettingOutlined,
  TableOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import type { ColumnsType } from 'antd/es/table';
import type {
  Dictionary,
  DictionaryColumn,
  DictionaryEntry,
  CreateDictionaryColumnRequest,
  FilterLeaf,
  SortEntry,
  UpsertDictionaryEntryRequest,
  DictionarySyncState,
} from '@lens/shared';
import { DICT_COLUMN_TYPES } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import {
  getDictionary,
  listDictionaries,
  updateDictionary,
  createColumn,
  updateColumn,
  deleteColumn,
  listEntries,
  createEntry,
  patchEntry,
  deleteEntry,
  getDictionarySync,
} from './api.ts';
import { DictCsvImportModal } from './DictCsvImportModal.tsx';
import { DictionaryAccessTab } from './DictionaryAccessTab.tsx';
import { DictionarySyncTab } from './DictionarySyncTab.tsx';
import { AlfaPeopleSyncModal } from './AlfaPeopleSyncModal.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import styles from './DictionaryDetailPage.module.css';
import {
  applyFilters,
  applySorts,
  buildGroupTree,
  CellEditor,
  FilterBar,
  GroupingPopover,
  ResizableTitle,
  type FieldLike,
  type GroupNode,
  type GroupingLevelLike,
  type ReferenceOption,
} from '../../shared/table';
import { dictColumnToField } from './columnAdapter.ts';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

// ─── Constants ────────────────────────────────────────────────────────────────

const COLUMN_TYPE_LABELS: Record<string, string> = {
  text: 'Текст',
  number: 'Число',
  boolean: 'Да/Нет',
  select: 'Выбор',
  multi_select: 'Мульти-выбор',
  url: 'Ссылка',
  reference: 'Ссылка на справочник',
};

interface EntriesState {
  items: DictionaryEntry[];
  nextCursor: string | null;
}

interface AddColumnFormValues {
  name: string;
  slug: string;
  columnType: string;
  refDictionaryId?: string;
  refMultiple?: boolean;
}

interface AddEntryFormValues {
  [key: string]: string | boolean | string[] | undefined;
}

export function DictionaryDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [dictionary, setDictionary] = useState<Dictionary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Может ли текущий пользователь редактировать справочник (platform_admin ИЛИ
  // владелец ИЛИ редактор). Бэкенд считает это под пользователя и кладёт в DTO;
  // используем для сокрытия всех мутирующих контролов у наблюдателей.
  const canEdit = dictionary?.canEdit ?? false;

  useDocumentTitle(dictionary?.name, 'Справочники');

  const [entriesState, setEntriesState] = useState<EntriesState>({
    items: [],
    nextCursor: null,
  });

  const [keyColumnId, setKeyColumnId] = useState<string | null>(null);
  const [displayColumnId, setDisplayColumnId] = useState<string | null>(null);
  const [savingColumns, setSavingColumns] = useState(false);

  // ─── Persisted table state per-dictionary in localStorage ────────────────
  // Ключ `dict-table-state:${id}` хранит фильтры, сортировки, ширины,
  // видимость, порядок колонок, группировку и search-строку.
  const persistKey = id ? `dict-table-state:${id}` : null;
  type PersistedState = {
    filters: FilterLeaf[];
    sorts: SortEntry[];
    search: string;
    colWidths: Record<string, number>;
    colOrder: string[];
    hiddenCols: string[];
    grouping: GroupingLevelLike[];
    collapsedGroups: string[];
  };
  const loadPersisted = (): PersistedState => {
    const empty: PersistedState = {
      filters: [],
      sorts: [],
      search: '',
      colWidths: {},
      colOrder: [],
      hiddenCols: [],
      grouping: [],
      collapsedGroups: [],
    };
    if (!persistKey) return empty;
    try {
      const raw = localStorage.getItem(persistKey);
      if (!raw) return empty;
      const parsed = JSON.parse(raw) as Partial<PersistedState>;
      return { ...empty, ...parsed };
    } catch {
      return empty;
    }
  };
  const initialPersisted = useMemo(loadPersisted, [persistKey]);

  const [filters, setFilters] = useState<FilterLeaf[]>(initialPersisted.filters);
  const [sorts, setSorts] = useState<SortEntry[]>(initialPersisted.sorts);
  const [searchInput, setSearchInput] = useState<string>(initialPersisted.search);
  const [searchApplied, setSearchApplied] = useState<string>(initialPersisted.search);
  const [colWidths, setColWidths] = useState<Record<string, number>>(initialPersisted.colWidths);
  const [colOrder, setColOrder] = useState<string[]>(initialPersisted.colOrder);
  const [hiddenCols, setHiddenCols] = useState<string[]>(initialPersisted.hiddenCols);
  const [grouping, setGrouping] = useState<GroupingLevelLike[]>(initialPersisted.grouping);
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>(initialPersisted.collapsedGroups);

  const resetTableState = useCallback(() => {
    setFilters([]);
    setSorts([]);
    setSearchInput('');
    setColOrder([]);
    setHiddenCols([]);
    setGrouping([]);
    setCollapsedGroups([]);
  }, []);

  // Persist on change
  useEffect(() => {
    if (!persistKey) return;
    try {
      const state: PersistedState = {
        filters,
        sorts,
        search: searchApplied,
        colWidths,
        colOrder,
        hiddenCols,
        grouping,
        collapsedGroups,
      };
      localStorage.setItem(persistKey, JSON.stringify(state));
    } catch {
      /* quota */
    }
  }, [persistKey, filters, sorts, searchApplied, colWidths, colOrder, hiddenCols, grouping, collapsedGroups]);

  // Debounce search input → searchApplied (350ms)
  useEffect(() => {
    const t = setTimeout(() => setSearchApplied(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleEntryColResize = useCallback(
    (slug: string) => (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
      setColWidths((prev) => ({ ...prev, [slug]: size.width }));
    },
    [],
  );

  const [addColumnForm] = Form.useForm<AddColumnFormValues>();
  const [addingColumn, setAddingColumn] = useState(false);

  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [addEntryForm] = Form.useForm<AddEntryFormValues>();
  const [addingEntry, setAddingEntry] = useState(false);

  const [loadingMore, setLoadingMore] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  // Обновление из Alfa People: настройка и история — только тем, кто правит справочник.
  const [syncState, setSyncState] = useState<DictionarySyncState | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);
  // Вкладка — в состоянии страницы: load() на время загрузки снимает Tabs,
  // и без этого после импорта или обновления открывались бы «Колонки».
  const [activeTab, setActiveTab] = useState('columns');
  const [exporting, setExporting] = useState(false);

  // ─── Bulk selection / edit ────────────────────────────────────
  const [selectedEntryIds, setSelectedEntryIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditColumnId, setBulkEditColumnId] = useState<string | null>(null);
  const [bulkEditMode, setBulkEditMode] = useState<'replace' | 'add' | 'remove'>('replace');
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const [bulkEditForm] = Form.useForm<{ value: string | boolean | string[] | undefined }>();

  // ─── Inline edit name / description ───────────────────────────
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  // (Inline-режим editingEntryId / editEntryForm заменён на per-cell CellEditor.)

  // ─── Editing columns ──────────────────────────────────────────
  const [editColumnOpen, setEditColumnOpen] = useState(false);
  const [editColumnTarget, setEditColumnTarget] = useState<DictionaryColumn | null>(null);
  const [editColumnForm] = Form.useForm<{
    name: string;
    refDictionaryId?: string;
    refMultiple?: boolean;
    options?: string[];
  }>();
  const [savingColumn, setSavingColumn] = useState(false);

  // ─── All dictionaries (for reference column target selection) ──
  const [allDicts, setAllDicts] = useState<Array<{ id: string; name: string }>>([]);
  const [addColType, setAddColType] = useState<string>('text');

  useEffect(() => {
    listDictionaries().then((list) => setAllDicts(list.map((d) => ({ id: d.id, name: d.name })))).catch(() => {});
  }, []);

  // ─── Reference column data: dictId → { entries, displaySlug } ──
  const [refData, setRefData] = useState<
    Record<string, { entries: Array<{ id: string; display: string }>; multiple?: boolean }>
  >({});
  const columns = dictionary?.columns ?? [];

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [dict, entries] = await Promise.all([
        getDictionary(id),
        listEntries(id, { search: searchApplied || undefined }),
      ]);
      setDictionary(dict);
      setKeyColumnId(dict.keyColumnId);
      setDisplayColumnId(dict.displayColumnId);
      setEntriesState({ items: entries.items, nextCursor: entries.nextCursor });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message || 'Не удалось загрузить справочник'
          : 'Не удалось загрузить справочник';
      setError(msg);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const dictCanEdit = dictionary?.canEdit ?? false;
  const loadSync = useCallback(async () => {
    if (!id || !dictCanEdit) {
      setSyncState(null);
      return;
    }
    try {
      setSyncState(await getDictionarySync(id));
    } catch {
      setSyncState(null);
    }
  }, [id, dictCanEdit]);

  useEffect(() => {
    void loadSync();
  }, [loadSync]);

  // Re-fetch entries when debounced search changes (без перезагрузки dictionary).
  const isInitialSearchRef = useRef(true);
  useEffect(() => {
    if (!id) return;
    if (isInitialSearchRef.current) {
      isInitialSearchRef.current = false;
      return;
    }
    let cancelled = false;
    listEntries(id, { search: searchApplied || undefined })
      .then((entries) => {
        if (cancelled) return;
        setEntriesState({ items: entries.items, nextCursor: entries.nextCursor });
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [id, searchApplied]);

  // Load reference column target dictionaries
  useEffect(() => {
    if (!dictionary) return;
    const refCols = (dictionary.columns ?? []).filter((c) => c.columnType === 'reference');
    if (refCols.length === 0) return;

    const loadRefs = async () => {
      const newRefData: typeof refData = {};
      for (const col of refCols) {
        const cfg = col.config as { dictionaryId?: string; multiple?: boolean } | null;
        if (!cfg?.dictionaryId) continue;
        try {
          const [targetDict, targetEntries] = await Promise.all([
            getDictionary(cfg.dictionaryId),
            listEntries(cfg.dictionaryId, { limit: 500 }),
          ]);
          const displaySlug = (targetDict.columns ?? []).find(
            (c) => c.id === targetDict.displayColumnId,
          )?.slug;
          newRefData[col.slug] = {
            multiple: cfg.multiple ?? false,
            entries: targetEntries.items.map((e) => ({
              id: e.id,
              display: displaySlug
                ? String((e.values as Record<string, unknown>)[displaySlug] ?? e.id.slice(0, 8))
                : e.id.slice(0, 8),
            })),
          };
        } catch {
          /* skip */
        }
      }
      setRefData(newRefData);
    };
    void loadRefs();
  }, [dictionary]);

  // ─── Save name ────────────────────────────────────────────────
  const handleSaveName = useCallback(async () => {
    if (!id || !nameValue.trim()) return;
    setSavingMeta(true);
    try {
      const updated = await updateDictionary(id, { name: nameValue.trim() });
      setDictionary(updated);
      setEditingName(false);
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSavingMeta(false);
    }
  }, [id, nameValue]);

  // ─── Save description ─────────────────────────────────────────
  const handleSaveDesc = useCallback(async () => {
    if (!id) return;
    setSavingMeta(true);
    try {
      const updated = await updateDictionary(id, { description: descValue.trim() || null });
      setDictionary(updated);
      setEditingDesc(false);
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSavingMeta(false);
    }
  }, [id, descValue]);

  const handleAddColumn = useCallback(
    async (values: AddColumnFormValues) => {
      if (!id || !dictionary) return;
      setAddingColumn(true);
      try {
        const config: Record<string, unknown> =
          values.columnType === 'reference' && values.refDictionaryId
            ? { dictionaryId: values.refDictionaryId, multiple: values.refMultiple ?? false }
            : {};
        const data: CreateDictionaryColumnRequest = {
          name: values.name,
          slug: values.slug,
          columnType: values.columnType as CreateDictionaryColumnRequest['columnType'],
          config,
        };
        const col = await createColumn(id, data);
        setDictionary((d) => d ? { ...d, columns: [...d.columns, col] } : d);
        addColumnForm.resetFields();
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Не удалось создать колонку');
      } finally {
        setAddingColumn(false);
      }
    },
    [id, dictionary, addColumnForm],
  );

  // ─── Open / save edit column modal ────────────────────────────
  const openEditColumn = useCallback((col: DictionaryColumn) => {
    setEditColumnTarget(col);
    const cfg = (col.config ?? {}) as {
      dictionaryId?: string;
      multiple?: boolean;
      options?: string[];
    };
    editColumnForm.setFieldsValue({
      name: col.name,
      refDictionaryId: cfg.dictionaryId,
      refMultiple: cfg.multiple === true,
      options: Array.isArray(cfg.options) ? cfg.options : [],
    });
    setEditColumnOpen(true);
  }, [editColumnForm]);

  const handleSaveEditColumn = useCallback(async () => {
    if (!id || !editColumnTarget) return;
    let values: { name: string; refDictionaryId?: string; refMultiple?: boolean; options?: string[] };
    try {
      values = await editColumnForm.validateFields();
    } catch {
      return;
    }
    setSavingColumn(true);
    try {
      const patch: { name?: string; config?: Record<string, unknown> } = {
        name: values.name.trim(),
      };
      if (editColumnTarget.columnType === 'reference') {
        const prevCfg = (editColumnTarget.config ?? {}) as Record<string, unknown>;
        patch.config = {
          ...prevCfg,
          dictionaryId: values.refDictionaryId ?? prevCfg.dictionaryId,
          multiple: values.refMultiple === true,
        };
      }
      if (editColumnTarget.columnType === 'select' || editColumnTarget.columnType === 'multi_select') {
        const prevCfg = (editColumnTarget.config ?? {}) as Record<string, unknown>;
        const cleaned = Array.from(
          new Set((values.options ?? []).map((o) => String(o).trim()).filter(Boolean)),
        );
        patch.config = { ...prevCfg, options: cleaned };
      }
      const updated = await updateColumn(id, editColumnTarget.id, patch);
      setDictionary((d) =>
        d ? { ...d, columns: d.columns.map((c) => c.id === updated.id ? updated : c) } : d,
      );
      setEditColumnOpen(false);
      setEditColumnTarget(null);
      editColumnForm.resetFields();
      void message.success('Колонка обновлена');
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSavingColumn(false);
    }
  }, [id, editColumnTarget, editColumnForm]);

  const handleDeleteColumn = useCallback(
    (colId: string) => {
      Modal.confirm({
        title: 'Удалить колонку?',
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: async () => {
          if (!id) return;
          await deleteColumn(id, colId);
          setDictionary((d) =>
            d ? { ...d, columns: d.columns.filter((c) => c.id !== colId) } : d,
          );
        },
      });
    },
    [id],
  );

  const handleSaveKeyDisplay = useCallback(async () => {
    if (!id) return;
    setSavingColumns(true);
    try {
      const updated = await updateDictionary(id, {
        keyColumnId: keyColumnId ?? undefined,
        displayColumnId: displayColumnId ?? undefined,
      });
      setDictionary(updated);
      setKeyColumnId(updated.keyColumnId);
      setDisplayColumnId(updated.displayColumnId);
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSavingColumns(false);
    }
  }, [id, keyColumnId, displayColumnId]);

  const handleAddEntry = useCallback(
    async (values: AddEntryFormValues) => {
      if (!id || !dictionary) return;
      setAddingEntry(true);
      try {
        const entryValues: Record<string, unknown> = {};
        for (const col of dictionary.columns) {
          const raw = values[col.slug];
          if (col.columnType === 'boolean') {
            entryValues[col.slug] = raw === true || raw === 'true';
          } else if (col.columnType === 'number') {
            entryValues[col.slug] = raw !== undefined && raw !== '' ? Number(raw) : undefined;
          } else {
            entryValues[col.slug] = raw;
          }
        }
        const data: UpsertDictionaryEntryRequest = { values: entryValues };
        const created = await createEntry(id, data);
        setEntriesState((s) => ({ ...s, items: [...s.items, created] }));
        setEntryModalOpen(false);
        addEntryForm.resetFields();
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Не удалось создать запись');
      } finally {
        setAddingEntry(false);
      }
    },
    [id, dictionary, addEntryForm],
  );

  // ─── Save single cell value via PATCH ─────────────────────────
  const handleSaveCell = useCallback(
    async (entryId: string, slug: string, value: unknown) => {
      if (!id) return;
      try {
        const updated = await patchEntry(id, entryId, { values: { [slug]: value } });
        setEntriesState((s) => ({
          ...s,
          items: s.items.map((e) => (e.id === entryId ? updated : e)),
        }));
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Не удалось обновить значение');
      }
    },
    [id],
  );

  const handleDeleteEntry = useCallback(
    (entryId: string) => {
      Modal.confirm({
        title: 'Удалить запись?',
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: async () => {
          if (!id) return;
          await deleteEntry(id, entryId);
          setEntriesState((s) => ({
            ...s,
            items: s.items.filter((e) => e.id !== entryId),
          }));
        },
      });
    },
    [id],
  );

  const handleBulkDelete = useCallback(() => {
    if (!id || selectedEntryIds.length === 0) return;
    const ids = [...selectedEntryIds];
    Modal.confirm({
      title: `Удалить записи: ${ids.length}?`,
      content: 'Это действие нельзя отменить.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        setBulkDeleting(true);
        const failed: string[] = [];
        await Promise.all(
          ids.map((entryId) =>
            deleteEntry(id, entryId).catch(() => {
              failed.push(entryId);
            }),
          ),
        );
        setEntriesState((s) => ({
          ...s,
          items: s.items.filter((e) => !ids.includes(e.id) || failed.includes(e.id)),
        }));
        setSelectedEntryIds((sel) => sel.filter((eid) => failed.includes(eid)));
        setBulkDeleting(false);
        if (failed.length > 0) {
          void message.error(`Не удалось удалить записей: ${failed.length}`);
        } else {
          void message.success(`Удалено записей: ${ids.length}`);
        }
      },
    });
  }, [id, selectedEntryIds]);

  const handleBulkEditApply = useCallback(async () => {
    if (!id || !dictionary || !bulkEditColumnId || selectedEntryIds.length === 0) return;
    const col = dictionary.columns.find((c) => c.id === bulkEditColumnId);
    if (!col) return;
    const ids = [...selectedEntryIds];
    const raw = bulkEditForm.getFieldValue('value');
    const isMultiRef =
      col.columnType === 'reference' && refData[col.slug]?.multiple === true;
    const isMultiSelect = col.columnType === 'multi_select';
    const isMultiValue = isMultiRef || isMultiSelect;

    const computeValue = (entry: DictionaryEntry): unknown => {
      if (isMultiValue && (bulkEditMode === 'add' || bulkEditMode === 'remove')) {
        const currentRaw = (entry.values as Record<string, unknown>)[col.slug];
        const current = Array.isArray(currentRaw) ? (currentRaw as string[]) : [];
        const picked = Array.isArray(raw) ? (raw as string[]) : [];
        if (bulkEditMode === 'add') {
          const set = new Set<string>(current);
          for (const v of picked) set.add(v);
          return Array.from(set);
        }
        const toRemove = new Set<string>(picked);
        return current.filter((v) => !toRemove.has(v));
      }
      if (col.columnType === 'boolean') {
        return raw === true || raw === 'true';
      }
      if (col.columnType === 'number') {
        return raw !== undefined && raw !== '' ? Number(raw) : null;
      }
      if (Array.isArray(raw)) {
        return raw;
      }
      return raw !== undefined && raw !== '' ? raw : null;
    };

    setBulkEditSaving(true);
    const failed: string[] = [];
    const updatedEntries: DictionaryEntry[] = [];
    await Promise.all(
      ids.map(async (entryId) => {
        const entry = entriesState.items.find((e) => e.id === entryId);
        if (!entry) {
          failed.push(entryId);
          return;
        }
        try {
          const updated = await patchEntry(id, entryId, {
            values: { [col.slug]: computeValue(entry) },
          });
          updatedEntries.push(updated);
        } catch {
          failed.push(entryId);
        }
      }),
    );
    setEntriesState((s) => ({
      ...s,
      items: s.items.map((e) => updatedEntries.find((u) => u.id === e.id) ?? e),
    }));
    setBulkEditSaving(false);
    if (failed.length > 0) {
      void message.error(`Не удалось обновить записей: ${failed.length}`);
    } else {
      void message.success(`Обновлено записей: ${ids.length}`);
      setBulkEditOpen(false);
      setBulkEditColumnId(null);
      setBulkEditMode('replace');
      bulkEditForm.resetFields();
      setSelectedEntryIds([]);
    }
  }, [id, dictionary, bulkEditColumnId, bulkEditMode, selectedEntryIds, bulkEditForm, refData, entriesState.items]);

  const handleLoadMore = useCallback(async () => {
    if (!id || !entriesState.nextCursor) return;
    setLoadingMore(true);
    try {
      const result = await listEntries(id, { cursor: entriesState.nextCursor });
      setEntriesState((s) => ({
        items: [...s.items, ...result.items],
        nextCursor: result.nextCursor,
      }));
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Ошибка загрузки');
    } finally {
      setLoadingMore(false);
    }
  }, [id, entriesState.nextCursor]);


  const columnTableColumns: ColumnsType<DictionaryColumn> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name, 'ru'),
      render: (val: string, record: DictionaryColumn) => (
        <Space size={6}>
          <span>{val}</span>
          {record.id === keyColumnId && (
            <Tooltip title="Ключевая колонка — используется для маппинга в Jira и upsert при импорте CSV">
              <Tag className={styles.keyTag}>Key</Tag>
            </Tooltip>
          )}
          {record.id === displayColumnId && (
            <Tooltip title="Отображаемая колонка — её значения видны в полях типа «Ссылка на справочник»">
              <Tag className={styles.displayTag}>Display</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      sorter: (a, b) => a.slug.localeCompare(b.slug),
      render: (val: string) => <code className={styles.slugPill}>{val}</code>,
    },
    {
      title: 'Тип',
      dataIndex: 'columnType',
      key: 'columnType',
      sorter: (a, b) =>
        (COLUMN_TYPE_LABELS[a.columnType] ?? a.columnType).localeCompare(
          COLUMN_TYPE_LABELS[b.columnType] ?? b.columnType,
          'ru',
        ),
      filters: DICT_COLUMN_TYPES.map((t) => ({ text: COLUMN_TYPE_LABELS[t] ?? t, value: t })),
      onFilter: (value, record) => record.columnType === value,
      render: (val: string) => COLUMN_TYPE_LABELS[val] ?? val,
    },
    {
      title: 'Порядок',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      sorter: (a, b) => a.sortOrder - b.sortOrder,
      defaultSortOrder: 'ascend',
    },
    ...(canEdit
      ? [
          {
            title: '',
            key: 'actions',
            width: 100,
            render: (_: unknown, record: DictionaryColumn) => (
              <Space size={4}>
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openEditColumn(record)}
                  data-testid={`dict-column-edit-${record.id}`}
                />
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteColumn(record.id)}
                  data-testid={`dict-column-delete-${record.id}`}
                  aria-label="Удалить колонку"
                />
              </Space>
            ),
          } as ColumnsType<DictionaryColumn>[number],
        ]
      : []),
  ];

  // ─── FieldLike-обёртки колонок для shared/table ──────────────
  const fieldsForTable: FieldLike[] = useMemo(
    () => columns.map(dictColumnToField),
    [columns],
  );
  const fieldBySlug = useMemo(() => {
    const m = new Map<string, FieldLike>();
    for (const f of fieldsForTable) m.set(f.slug, f);
    return m;
  }, [fieldsForTable]);
  const colBySlug = useMemo(() => {
    const m = new Map<string, DictionaryColumn>();
    for (const c of columns) m.set(c.slug, c);
    return m;
  }, [columns]);

  // resolvedRefs: map id → display label, формируется из refData
  const resolvedRefs = useMemo(() => {
    const m: Record<string, string> = {};
    for (const rd of Object.values(refData)) {
      for (const e of rd.entries) m[e.id] = e.display;
    }
    return m;
  }, [refData]);

  // Опции для FilterBar/CellEditor по reference-колонке
  const referenceOptionsForField = useCallback(
    async (field: FieldLike): Promise<ReferenceOption[]> => {
      const col = colBySlug.get(field.slug);
      if (!col || col.columnType !== 'reference') return [];
      const rd = refData[col.slug];
      if (rd) return rd.entries.map((e) => ({ value: e.id, label: e.display }));
      const cfg = (col.config ?? {}) as { dictionaryId?: string };
      if (!cfg.dictionaryId) return [];
      try {
        const [targetDict, targetEntries] = await Promise.all([
          getDictionary(cfg.dictionaryId),
          listEntries(cfg.dictionaryId, { limit: 500 }),
        ]);
        const displaySlug = (targetDict.columns ?? []).find(
          (c) => c.id === targetDict.displayColumnId,
        )?.slug;
        return targetEntries.items.map((e) => ({
          value: e.id,
          label: displaySlug
            ? String((e.values as Record<string, unknown>)[displaySlug] ?? e.id.slice(0, 8))
            : e.id.slice(0, 8),
        }));
      } catch {
        return [];
      }
    },
    [colBySlug, refData],
  );

  const renderReferenceLabel = useCallback(
    (col: DictionaryColumn, val: unknown): string => {
      const rd = refData[col.slug];
      if (!rd) return Array.isArray(val) ? val.join(', ') : String(val ?? '');
      if (Array.isArray(val)) {
        return val
          .map((v) => rd.entries.find((e) => e.id === v)?.display ?? String(v))
          .join(', ');
      }
      if (typeof val === 'string') {
        return rd.entries.find((e) => e.id === val)?.display ?? val;
      }
      return '';
    },
    [refData],
  );

  // Order/visibility — синхронизируем с актуальными колонками
  const allSlugs = useMemo(() => columns.map((c) => c.slug), [columns]);
  const orderedSlugs = useMemo(() => {
    const base = colOrder.length > 0 ? colOrder.filter((s) => allSlugs.includes(s)) : allSlugs;
    const missing = allSlugs.filter((s) => !base.includes(s));
    return [...base, ...missing].filter((s) => !hiddenCols.includes(s));
  }, [colOrder, allSlugs, hiddenCols]);

  const entryTableColumns: ColumnsType<DictionaryEntry> = [
    ...orderedSlugs
      .map((slug) => colBySlug.get(slug))
      .filter((c): c is DictionaryColumn => Boolean(c))
      .map((col) => {
        const colWidth = colWidths[col.slug] ?? 180;
        const field = fieldBySlug.get(col.slug)!;
        return {
          title: col.name,
          key: col.slug,
          dataIndex: col.slug,
          width: colWidth,
          ellipsis: { showTitle: true },
          onHeaderCell: () => ({
            width: colWidth,
            onResize: handleEntryColResize(col.slug),
            'data-col-slug': col.slug,
          }),
          render: (_: unknown, record: DictionaryEntry) => {
            const val = (record.values as Record<string, unknown>)[col.slug];
            return (
              <CellEditor
                field={field}
                value={val}
                resolvedRefs={resolvedRefs}
                referenceOptions={
                  col.columnType === 'reference' && refData[col.slug]
                    ? refData[col.slug]!.entries.map((e) => ({
                        value: e.id,
                        label: e.display,
                      }))
                    : undefined
                }
                loadReferenceOptions={referenceOptionsForField}
                onSave={(next) => void handleSaveCell(record.id, col.slug, next)}
                readOnly={!canEdit}
              />
            );
          },
        } as ColumnsType<DictionaryEntry>[number];
      }),
    ...(canEdit
      ? [
          {
            title: '',
            key: 'actions',
            width: 64,
            render: (_: unknown, record: DictionaryEntry) => (
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteEntry(record.id)}
                data-testid={`dict-entry-delete-${record.id}`}
                aria-label="Удалить запись"
              />
            ),
          } as ColumnsType<DictionaryEntry>[number],
        ]
      : []),
  ];

  const columnOptions = columns.map((c) => ({ label: c.name, value: c.id }));

  const columnsTab = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card className={styles.sectionCard}>
        <Table<DictionaryColumn>
          rowKey="id"
          columns={columnTableColumns}
          dataSource={columns}
          pagination={false}
          size="small"
        />
      </Card>

      {canEdit && (
      <div className={styles.addSection}>
        <Typography.Title level={5} className={styles.addSectionTitle}>
          <PlusOutlined style={{ marginRight: 6 }} />
          Добавить колонку
        </Typography.Title>
        <Form
          form={addColumnForm}
          layout="inline"
          onFinish={(v) => void handleAddColumn(v)}
        >
          <Form.Item
            name="name"
            rules={[{ required: true, message: 'Обязательное поле' }]}
          >
            <Input placeholder="Название" data-testid="dict-column-name-input" />
          </Form.Item>
          <Form.Item
            name="slug"
            rules={[
              { required: true, message: 'Обязательное поле' },
              { pattern: /^[a-z][a-z0-9_]{0,49}$/, message: 'Строчные латинские, цифры, _' },
            ]}
          >
            <Input placeholder="slug" data-testid="dict-column-slug-input" />
          </Form.Item>
          <Form.Item
            name="columnType"
            rules={[{ required: true, message: 'Выберите тип' }]}
          >
            <Select
              placeholder="Тип"
              style={{ width: 180 }}
              data-testid="dict-column-type-select"
              onChange={(v) => setAddColType(v)}
              options={DICT_COLUMN_TYPES.map((t) => ({
                label: COLUMN_TYPE_LABELS[t] ?? t,
                value: t,
              }))}
            />
          </Form.Item>
          {addColType === 'reference' && (
            <>
              <Form.Item
                name="refDictionaryId"
                rules={[{ required: true, message: 'Выберите справочник' }]}
              >
                <Select
                  placeholder="Целевой справочник"
                  style={{ width: 200 }}
                  showSearch
                  optionFilterProp="label"
                  options={allDicts.map((d) => ({
                    value: d.id,
                    label: d.id === id ? `${d.name} (этот же справочник)` : d.name,
                  }))}
                />
              </Form.Item>
              <Form.Item name="refMultiple" valuePropName="checked">
                <input type="checkbox" /> Множественный
              </Form.Item>
            </>
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={addingColumn}
              data-testid="dict-column-submit"
            >
              Добавить
            </Button>
          </Form.Item>
        </Form>
      </div>
      )}

      {canEdit && (
      <div className={styles.keyDisplaySection}>
        <Typography.Title level={5} className={styles.keyDisplayTitle}>
          <SettingOutlined style={{ marginRight: 6 }} />
          Ключевая / Отображаемая колонка
        </Typography.Title>
        <Space>
          <Select
            placeholder="Ключевая колонка"
            style={{ width: 200 }}
            options={columnOptions}
            value={keyColumnId ?? undefined}
            onChange={(value) => setKeyColumnId(value ?? null)}
            allowClear
            data-testid="dict-key-column-select"
          />
          <Select
            placeholder="Отображаемая колонка"
            style={{ width: 200 }}
            options={columnOptions}
            value={displayColumnId ?? undefined}
            onChange={(value) => setDisplayColumnId(value ?? null)}
            allowClear
            data-testid="dict-display-column-select"
          />
          <Button
            type="primary"
            onClick={() => void handleSaveKeyDisplay()}
            loading={savingColumns}
            data-testid="dict-columns-save-btn"
          >
            Сохранить
          </Button>
        </Space>
      </div>
      )}

      <Modal
        title="Редактировать колонку"
        open={editColumnOpen}
        onCancel={() => {
          setEditColumnOpen(false);
          setEditColumnTarget(null);
          editColumnForm.resetFields();
        }}
        onOk={() => void handleSaveEditColumn()}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={savingColumn}
        destroyOnHidden
      >
        <Form form={editColumnForm} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: 'Обязательное поле' }]}
          >
            <Input data-testid="dict-column-edit-name-input" />
          </Form.Item>
          {editColumnTarget?.columnType === 'reference' && (
            <>
              <Form.Item name="refDictionaryId" label="Целевой справочник">
                <Select
                  disabled
                  options={allDicts.map((d) => ({
                    value: d.id,
                    label: d.id === id ? `${d.name} (этот же справочник)` : d.name,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="refMultiple"
                valuePropName="checked"
                tooltip="Разрешить выбор нескольких значений в одной записи"
              >
                <Checkbox data-testid="dict-column-edit-multiple-input">
                  Множественный выбор
                </Checkbox>
              </Form.Item>
            </>
          )}
          {(editColumnTarget?.columnType === 'select' ||
            editColumnTarget?.columnType === 'multi_select') && (
            <Form.Item
              name="options"
              label="Варианты выбора"
              tooltip="Введите значение и нажмите Enter. Вводите запятую или Enter для следующего варианта."
            >
              <Select
                mode="tags"
                tokenSeparators={[',']}
                placeholder="Например: низкий, средний, высокий"
                data-testid="dict-column-edit-options-input"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Space>
  );

  // Get raw value of a field for shared-table evaluators
  const getEntryRawValue = useCallback(
    (entry: DictionaryEntry, slug: string): unknown =>
      (entry.values as Record<string, unknown>)[slug],
    [],
  );

  const resolveFieldForSlug = useCallback(
    (slug: string) => fieldBySlug.get(slug),
    [fieldBySlug],
  );

  // Применяем фильтры → сортировки на клиенте (search уходит на сервер).
  const processedEntries = useMemo(() => {
    let rows = entriesState.items;
    rows = applyFilters(rows, filters, {
      getValue: getEntryRawValue,
      resolveField: resolveFieldForSlug,
    });
    rows = applySorts(rows, sorts, {
      getValue: getEntryRawValue,
      resolveField: resolveFieldForSlug,
      resolveLabel: (slug, raw) => {
        const col = colBySlug.get(slug);
        if (!col) return Array.isArray(raw) ? raw.join(',') : String(raw ?? '');
        if (col.columnType === 'reference') return renderReferenceLabel(col, raw);
        return Array.isArray(raw) ? raw.join(',') : String(raw ?? '');
      },
    });
    return rows;
  }, [entriesState.items, filters, sorts, getEntryRawValue, resolveFieldForSlug, colBySlug, renderReferenceLabel]);

  // ─── Группировка строк (multi-level) ───────────────────────────────────────
  const resolveGroupLabel = useCallback(
    (fieldSlug: string, rawKey: string): string => {
      const col = colBySlug.get(fieldSlug);
      if (!col) return rawKey;
      if (col.columnType === 'boolean') return rawKey === 'true' ? 'Да' : 'Нет';
      if (col.columnType === 'reference') return renderReferenceLabel(col, rawKey);
      if (col.columnType === 'select' || col.columnType === 'multi_select') {
        const opts =
          (col.config as { options?: Array<{ value: string; label: string }> } | null)
            ?.options ?? [];
        return opts.find((o) => o.value === rawKey)?.label ?? rawKey;
      }
      return rawKey;
    },
    [colBySlug, renderReferenceLabel],
  );

  const groupTree = useMemo(() => {
    if (grouping.length === 0) return null;
    return buildGroupTree<DictionaryEntry>(processedEntries, grouping, {
      getRawValue: getEntryRawValue,
      resolveField: resolveFieldForSlug,
      resolveLabel: resolveGroupLabel,
    });
  }, [grouping, processedEntries, getEntryRawValue, resolveFieldForSlug, resolveGroupLabel]);

  const toggleGroupCollapsed = useCallback((key: string) => {
    setCollapsedGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const dictFields = useMemo(() => columns.map(dictColumnToField), [columns]);

  // ─── Экспорт записей справочника в Excel (.xlsx) ─────────────
  const formatCellForExport = useCallback(
    (col: DictionaryColumn, val: unknown): string | number => {
      if (val === undefined || val === null || val === '') return '';
      if (col.columnType === 'boolean') {
        return val === true || val === 'true' ? 'Да' : 'Нет';
      }
      if (col.columnType === 'number') {
        return typeof val === 'number' ? val : Number(val);
      }
      if (col.columnType === 'reference') {
        return renderReferenceLabel(col, val);
      }
      if (Array.isArray(val)) return val.join(', ');
      return String(val);
    },
    [renderReferenceLabel],
  );

  const handleExportExcel = useCallback(async () => {
    if (!id || !dictionary) return;
    setExporting(true);
    try {
      // Тянем все записи справочника постранично (с учётом server-side
      // поиска), затем применяем те же фильтры/сортировки, что и в таблице,
      // — чтобы выгрузка совпадала с видимым результатом фильтрации, но не
      // ограничивалась подгруженной в таблицу страницей.
      const all: DictionaryEntry[] = [];
      let cursor: string | null = null;
      do {
        const res = await listEntries(id, {
          cursor: cursor ?? undefined,
          limit: 500,
          search: searchApplied || undefined,
        });
        all.push(...res.items);
        cursor = res.nextCursor;
      } while (cursor);

      let rows = applyFilters(all, filters, {
        getValue: getEntryRawValue,
        resolveField: resolveFieldForSlug,
      });
      rows = applySorts(rows, sorts, {
        getValue: getEntryRawValue,
        resolveField: resolveFieldForSlug,
        resolveLabel: (slug, raw) => {
          const col = colBySlug.get(slug);
          if (!col) return Array.isArray(raw) ? raw.join(',') : String(raw ?? '');
          if (col.columnType === 'reference') return renderReferenceLabel(col, raw);
          return Array.isArray(raw) ? raw.join(',') : String(raw ?? '');
        },
      });

      // Колонки — в порядке и видимости текущей таблицы.
      const exportCols = orderedSlugs
        .map((slug) => colBySlug.get(slug))
        .filter((c): c is DictionaryColumn => Boolean(c));
      const aoa: (string | number)[][] = [exportCols.map((c) => c.name)];
      for (const entry of rows) {
        const values = entry.values as Record<string, unknown>;
        aoa.push(exportCols.map((c) => formatCellForExport(c, values[c.slug])));
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = exportCols.map((c) => ({ wch: Math.min(Math.max(c.name.length + 2, 12), 40) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Справочник');
      const safeName = (dictionary.name || 'dictionary')
        .replace(/[\\/:*?"<>|]/g, '_')
        .slice(0, 80);
      XLSX.writeFile(wb, `${safeName}.xlsx`);
      void message.success(`Выгружено записей: ${rows.length}`);
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Не удалось выгрузить в Excel');
    } finally {
      setExporting(false);
    }
  }, [
    id,
    dictionary,
    searchApplied,
    filters,
    sorts,
    orderedSlugs,
    colBySlug,
    getEntryRawValue,
    resolveFieldForSlug,
    renderReferenceLabel,
    formatCellForExport,
  ]);

  // Нейтральная лесенка подложек по глубине вынесена в CSS-модуль
  // (.groupDepth0..4 — мягкие statusCategory.*.bg), без сырых hex в TSX.
  const GROUP_DEPTH_CLASSES = [
    styles.groupDepth0,
    styles.groupDepth1,
    styles.groupDepth2,
    styles.groupDepth3,
    styles.groupDepth4,
  ];
  const groupRowSelection = {
    selectedRowKeys: selectedEntryIds,
    onChange: (keys: React.Key[]) =>
      setSelectedEntryIds(keys.map((k) => String(k))),
  };
  let groupTableHeaderShown = false;

  const renderGroupNodes = (nodes: GroupNode<DictionaryEntry>[]): React.ReactNode =>
    nodes.map((node) => {
      const collapsed = collapsedGroups.includes(node.key);
      const depthClass = GROUP_DEPTH_CLASSES[node.depth % GROUP_DEPTH_CLASSES.length];
      const isLeaf = !node.children;
      return (
        <div key={node.key} style={{ marginLeft: node.depth * 16, marginBottom: 4 }}>
          <div
            className={`${styles.groupHeader} ${node.depth === 0 ? styles.groupHeaderRoot : ''} ${depthClass}`}
            onClick={() => toggleGroupCollapsed(node.key)}
            data-testid={`dict-group-header-${node.key}`}
          >
            <span
              className={`${styles.groupChevron} ${collapsed ? styles.groupChevronCollapsed : ''}`}
            >
              <CaretDownOutlined />
            </span>
            <span className={styles.groupFieldName}>{node.fieldName}:</span>
            <span className={styles.groupLabel}>{node.label}</span>
            <Tag className={styles.groupCount}>{node.items.length}</Tag>
          </div>
          {!collapsed && isLeaf && (() => {
            const needHeader = !groupTableHeaderShown;
            groupTableHeaderShown = true;
            return (
              <Table<DictionaryEntry>
                rowKey="id"
                className="lens-dict-entries-table"
                columns={entryTableColumns}
                dataSource={node.items}
                pagination={false}
                size="small"
                tableLayout="fixed"
                scroll={{ x: 'max-content' }}
                showHeader={needHeader}
                components={{ header: { cell: ResizableTitle } }}
                rowSelection={canEdit ? groupRowSelection : undefined}
              />
            );
          })()}
          {!collapsed && node.children && renderGroupNodes(node.children)}
        </div>
      );
    });

  const entriesTab = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div className={styles.entriesHeader}>
        <div className={styles.filterBarSlot}>
          <FilterBar
            fields={dictFields}
            filters={filters}
            onFiltersChange={setFilters}
            sorts={sorts}
            onSortsChange={setSorts}
            search={searchInput}
            onSearchChange={setSearchInput}
            ensureReferenceOptions={referenceOptionsForField}
            resolvedRefs={resolvedRefs}
            extraButtons={
              <GroupingPopover
                fields={dictFields}
                levels={grouping}
                onAdd={(slug) =>
                  setGrouping([...grouping, { fieldSlug: slug, order: 'name' }])
                }
                onRemove={(slug) =>
                  setGrouping(grouping.filter((g) => g.fieldSlug !== slug))
                }
                onReorder={(from, to) => {
                  const next = [...grouping];
                  const [moved] = next.splice(from, 1);
                  if (!moved) return;
                  next.splice(to, 0, moved);
                  setGrouping(next);
                }}
                onChangeOrder={(slug, order) =>
                  setGrouping(
                    grouping.map((g) => (g.fieldSlug === slug ? { ...g, order } : g)),
                  )
                }
                onClearAll={() => setGrouping([])}
              />
            }
          />
        </div>
        <Tooltip title="Обновить таблицу">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => void load()}
            data-testid="dict-entries-refresh"
            aria-label="Обновить"
          />
        </Tooltip>
        <Tooltip title="Сбросить вид (фильтры, сортировки, группировку)">
          <Button
            size="small"
            onClick={resetTableState}
            data-testid="dict-table-reset-btn"
          >
            Сбросить вид
          </Button>
        </Tooltip>
        {canEdit && selectedEntryIds.length > 0 && (
          <>
            <Typography.Text type="secondary" data-testid="dict-bulk-selection-count">
              Выбрано: {selectedEntryIds.length}
            </Typography.Text>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setBulkEditColumnId(null);
                bulkEditForm.resetFields();
                setBulkEditOpen(true);
              }}
              data-testid="dict-bulk-edit-btn"
            >
              Редактировать
            </Button>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={bulkDeleting}
              onClick={handleBulkDelete}
              data-testid="dict-bulk-delete-btn"
            >
              Удалить
            </Button>
          </>
        )}
        {canEdit && syncState?.config && syncState.config.managers.length > 0 && (
          <Button
            size="small"
            icon={<CloudSyncOutlined />}
            onClick={() => setSyncOpen(true)}
            data-testid="dict-ap-sync-btn"
          >
            Обновить из Alfa People
          </Button>
        )}
        {canEdit && (
          <Button
            size="small"
            icon={<UploadOutlined />}
            onClick={() => setCsvImportOpen(true)}
            data-testid="dict-import-btn"
          >
            Импорт CSV
          </Button>
        )}
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={exporting}
          onClick={() => void handleExportExcel()}
          data-testid="dict-export-excel-btn"
        >
          Экспорт в Excel
        </Button>
        {canEdit && (
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setEntryModalOpen(true)}
            data-testid="dict-entry-create-btn"
          >
            Добавить запись
          </Button>
        )}
      </div>

      <Card className={styles.sectionCard}>
        {groupTree ? (
          <div data-testid="dict-grouped-view">{renderGroupNodes(groupTree)}</div>
        ) : (
          <Table<DictionaryEntry>
            rowKey="id"
            className="lens-dict-entries-table"
            columns={entryTableColumns}
            dataSource={processedEntries}
            pagination={false}
            size="small"
            tableLayout="fixed"
            scroll={{ x: 'max-content' }}
            components={{ header: { cell: ResizableTitle } }}
            rowSelection={
              canEdit
                ? {
                    selectedRowKeys: selectedEntryIds,
                    onChange: (keys) => setSelectedEntryIds(keys.map((k) => String(k))),
                  }
                : undefined
            }
          />
        )}
      </Card>

      {entriesState.nextCursor !== null && (
        <div className={styles.loadMoreWrapper}>
          <Button
            onClick={() => void handleLoadMore()}
            loading={loadingMore}
            data-testid="dict-load-more-btn"
          >
            Загрузить ещё
          </Button>
        </div>
      )}

      <Modal
        title={`Массовое редактирование: ${selectedEntryIds.length}`}
        open={bulkEditOpen}
        onCancel={() => {
          setBulkEditOpen(false);
          setBulkEditColumnId(null);
          setBulkEditMode('replace');
          bulkEditForm.resetFields();
        }}
        footer={null}
        destroyOnHidden
      >
        <Form form={bulkEditForm} layout="vertical">
          <Form.Item label="Колонка">
            <Select
              placeholder="Выберите колонку"
              value={bulkEditColumnId ?? undefined}
              onChange={(v) => {
                setBulkEditColumnId(v);
                setBulkEditMode('replace');
                bulkEditForm.setFieldsValue({ value: undefined });
              }}
              options={columns
                .filter((c) => c.columnType !== 'reference' || refData[c.slug])
                .map((c) => ({ value: c.id, label: c.name }))}
              data-testid="dict-bulk-edit-column-select"
            />
          </Form.Item>
          {bulkEditColumnId && (() => {
            const col = columns.find((c) => c.id === bulkEditColumnId);
            if (!col) return null;
            const rd = col.columnType === 'reference' ? refData[col.slug] : undefined;
            const isMultiRef = col.columnType === 'reference' && rd?.multiple === true;
            const isMultiSelect = col.columnType === 'multi_select';
            const isMultiValue = isMultiRef || isMultiSelect;
            const modeLabels: Record<'replace' | 'add' | 'remove', string> = {
              replace: 'Заменить',
              add: 'Добавить',
              remove: 'Удалить',
            };
            const label =
              isMultiValue
                ? `${modeLabels[bulkEditMode]} значения «${col.name}»`
                : `Новое значение «${col.name}»`;

            const modeHint =
              isMultiValue
                ? bulkEditMode === 'add'
                  ? 'Выбранные значения будут добавлены к существующим (без дублей).'
                  : bulkEditMode === 'remove'
                    ? 'Выбранные значения будут удалены из существующих.'
                    : 'Текущие значения будут заменены выбранными. Пустой список очистит поле.'
                : 'Пустое значение очистит поле у выбранных записей.';

            return (
              <>
                {isMultiValue && (
                  <Form.Item label="Режим">
                    <Radio.Group
                      value={bulkEditMode}
                      onChange={(e) => setBulkEditMode(e.target.value)}
                      data-testid="dict-bulk-edit-mode"
                    >
                      <Radio.Button value="replace">Заменить</Radio.Button>
                      <Radio.Button value="add">Добавить</Radio.Button>
                      <Radio.Button value="remove">Удалить</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                )}
                {col.columnType === 'boolean' ? (
                  <Form.Item name="value" label={label}>
                    <Select
                      allowClear
                      placeholder="Очистить / выбрать"
                      options={[
                        { label: 'Да', value: true },
                        { label: 'Нет', value: false },
                      ]}
                      data-testid="dict-bulk-edit-value-input"
                    />
                  </Form.Item>
                ) : col.columnType === 'number' ? (
                  <Form.Item name="value" label={label}>
                    <Input type="number" data-testid="dict-bulk-edit-value-input" />
                  </Form.Item>
                ) : col.columnType === 'reference' && rd ? (
                  <Form.Item name="value" label={label}>
                    <Select
                      mode={rd.multiple ? 'multiple' : undefined}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Выберите..."
                      options={rd.entries.map((e) => ({ value: e.id, label: e.display }))}
                      data-testid="dict-bulk-edit-value-input"
                    />
                  </Form.Item>
                ) : isMultiSelect || col.columnType === 'select' ? (() => {
                  const cfg = (col.config ?? {}) as { options?: string[] };
                  const hasOptions = Array.isArray(cfg.options) && cfg.options.length > 0;
                  return (
                    <Form.Item name="value" label={label}>
                      <Select
                        mode={
                          isMultiSelect ? (hasOptions ? 'multiple' : 'tags') : undefined
                        }
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        tokenSeparators={isMultiSelect ? [','] : undefined}
                        placeholder={hasOptions ? 'Выберите...' : 'Введите значение...'}
                        options={hasOptions ? cfg.options!.map((o) => ({ label: o, value: o })) : undefined}
                        data-testid="dict-bulk-edit-value-input"
                      />
                    </Form.Item>
                  );
                })() : (
                  <Form.Item name="value" label={label}>
                    <Input data-testid="dict-bulk-edit-value-input" />
                  </Form.Item>
                )}
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {modeHint}
                </Typography.Text>
              </>
            );
          })()}
          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                onClick={() => {
                  setBulkEditOpen(false);
                  setBulkEditColumnId(null);
                  bulkEditForm.resetFields();
                }}
              >
                Отмена
              </Button>
              <Button
                type="primary"
                loading={bulkEditSaving}
                disabled={!bulkEditColumnId}
                onClick={() => void handleBulkEditApply()}
                data-testid="dict-bulk-edit-submit"
              >
                Применить
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Добавить запись"
        open={entryModalOpen}
        onCancel={() => {
          setEntryModalOpen(false);
          addEntryForm.resetFields();
        }}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={addEntryForm}
          layout="vertical"
          onFinish={(v) => void handleAddEntry(v)}
        >
          {columns.map((col) => {
            const rd = col.columnType === 'reference' ? refData[col.slug] : undefined;
            return (
              <Form.Item key={col.slug} name={col.slug} label={col.name}>
                {col.columnType === 'boolean' ? (
                  <Select
                    options={[
                      { label: 'Да', value: true },
                      { label: 'Нет', value: false },
                    ]}
                    data-testid={`dict-entry-input-${col.slug}`}
                  />
                ) : col.columnType === 'number' ? (
                  <Input
                    type="number"
                    data-testid={`dict-entry-input-${col.slug}`}
                  />
                ) : col.columnType === 'reference' && rd ? (
                  <Select
                    mode={rd.multiple ? 'multiple' : undefined}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Выберите..."
                    options={rd.entries.map((e) => ({ value: e.id, label: e.display }))}
                    data-testid={`dict-entry-input-${col.slug}`}
                  />
                ) : col.columnType === 'multi_select' || col.columnType === 'select' ? (() => {
                  const cfg = (col.config ?? {}) as { options?: string[] };
                  const hasOptions = Array.isArray(cfg.options) && cfg.options.length > 0;
                  return (
                    <Select
                      mode={
                        col.columnType === 'multi_select'
                          ? (hasOptions ? 'multiple' : 'tags')
                          : undefined
                      }
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      tokenSeparators={col.columnType === 'multi_select' ? [','] : undefined}
                      placeholder={hasOptions ? 'Выберите...' : 'Введите значение...'}
                      options={hasOptions ? cfg.options!.map((o) => ({ label: o, value: o })) : undefined}
                      data-testid={`dict-entry-input-${col.slug}`}
                    />
                  );
                })() : (
                  <Input data-testid={`dict-entry-input-${col.slug}`} />
                )}
              </Form.Item>
            );
          })}
          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => { setEntryModalOpen(false); addEntryForm.resetFields(); }}>
                Отмена
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={addingEntry}
                data-testid="dict-entry-submit"
              >
                Сохранить
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );

  const syncTab =
    canEdit && dictionary && syncState
      ? [
          {
            key: 'alfa-people',
            label: (
              <span data-testid="dict-sync-tab-label">
                <CloudSyncOutlined style={{ marginRight: 6 }} />
                Alfa People
              </span>
            ),
            children: (
              <DictionarySyncTab
                dictionary={dictionary}
                config={syncState.config}
                runs={syncState.runs}
                onSaved={(config) => setSyncState((prev) => (prev ? { ...prev, config } : prev))}
                onRunSync={() => setSyncOpen(true)}
              />
            ),
          },
        ]
      : [];

  const tabItems = [
    {
      key: 'columns',
      label: (
        <span data-testid="dict-columns-tab">
          <TableOutlined style={{ marginRight: 6 }} />
          Колонки
        </span>
      ),
      children: columnsTab,
    },
    {
      key: 'entries',
      label: (
        <span data-testid="dict-entries-tab">
          <UnorderedListOutlined style={{ marginRight: 6 }} />
          Записи
        </span>
      ),
      children: entriesTab,
    },
    {
      key: 'access',
      label: (
        <span data-testid="dict-access-tab-label">
          <SafetyOutlined style={{ marginRight: 6 }} />
          Права и доступ
        </span>
      ),
      children: dictionary ? (
        <DictionaryAccessTab
          dictionary={dictionary}
          currentUserId={user?.id ?? null}
          isPlatformAdmin={user?.isPlatformAdmin ?? false}
          onDictionaryChange={setDictionary}
        />
      ) : null,
    },
    ...syncTab,
  ];

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: 64 }}
        data-testid="dict-detail-page"
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error || !dictionary) {
    return (
      <div data-testid="dict-detail-page">
        <Alert
          type="error"
          message="Не удалось загрузить справочник"
          description={error ?? ''}
          showIcon
          action={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => void load()}
              data-testid="dict-detail-reload-btn"
            >
              Повторить
            </Button>
          }
        />
      </div>
    );
  }

  // Ограничение видимости: справочник во вкладке видят только создатель,
  // владелец, редакторы, наблюдатели и админ платформы. Бэкенд отдаёт флаг
  // canView под текущего пользователя; сам GET :id остаётся открытым (его
  // дёргают reference-поля), поэтому гейтим показ страницы здесь.
  if (!dictionary.canView) {
    return (
      <div data-testid="dict-detail-page">
        <Link to="/dictionaries" className={styles.backLink}>
          <ArrowLeftOutlined />
          Все справочники
        </Link>
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="Нет доступа к справочнику"
          description="Этот справочник виден только его создателю, владельцу, назначенным редакторам и наблюдателям, а также администратору платформы. Попросите владельца выдать вам доступ на просмотр."
          data-testid="dict-detail-no-access"
        />
      </div>
    );
  }

  return (
    <div data-testid="dict-detail-page">
      <Link to="/dictionaries" className={styles.backLink}>
        <ArrowLeftOutlined />
        Все справочники
      </Link>

      <div className={styles.pageHeader}>
        {editingName ? (
          <Space size={8}>
            <Input
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSaveName();
                if (e.key === 'Escape') setEditingName(false);
              }}
              autoFocus
              style={{ fontSize: 20, fontWeight: 600, width: 400 }}
            />
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={savingMeta}
              onClick={() => void handleSaveName()}
            />
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setEditingName(false)}
            />
          </Space>
        ) : canEdit ? (
          <h2
            className={styles.pageTitle}
            style={{ cursor: 'pointer' }}
            onClick={() => { setNameValue(dictionary.name); setEditingName(true); }}
            title="Нажмите для редактирования"
          >
            {dictionary.name}
            <EditOutlined className={styles.editIcon} />
          </h2>
        ) : (
          <h2 className={styles.pageTitle}>{dictionary.name}</h2>
        )}
      </div>
      {editingDesc ? (
        <Space size={8} style={{ marginBottom: 16 }}>
          <Input.TextArea
            value={descValue}
            onChange={(e) => setDescValue(e.target.value)}
            autoSize={{ minRows: 1, maxRows: 4 }}
            style={{ width: 500 }}
            autoFocus
          />
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            loading={savingMeta}
            onClick={() => void handleSaveDesc()}
          />
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={() => setEditingDesc(false)}
          />
        </Space>
      ) : canEdit ? (
        <p
          className={styles.description}
          style={{ cursor: 'pointer', minHeight: 22 }}
          onClick={() => { setDescValue(dictionary.description ?? ''); setEditingDesc(true); }}
          title="Нажмите для редактирования"
        >
          {dictionary.description || (
            <span className={styles.descPlaceholder}>Добавить описание</span>
          )}
          <EditOutlined className={styles.descEditIcon} />
        </p>
      ) : dictionary.description ? (
        <p className={styles.description} style={{ minHeight: 22 }}>
          {dictionary.description}
        </p>
      ) : null}
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      {dictionary && (
        <DictCsvImportModal
          open={csvImportOpen}
          onClose={() => setCsvImportOpen(false)}
          onDone={() => { setCsvImportOpen(false); void load(); }}
          dictId={dictionary.id}
          columns={columns}
          keyColumnId={dictionary.keyColumnId}
        />
      )}
      {dictionary && syncState?.config && (
        <AlfaPeopleSyncModal
          open={syncOpen}
          dictId={dictionary.id}
          config={syncState.config}
          onClose={(applied) => {
            setSyncOpen(false);
            // Записи перечитываем после закрытия: load() снимает страницу
            // спиннером, и окно с итогом пропало бы.
            if (applied) void load();
          }}
          onDone={() => void loadSync()}
        />
      )}
    </div>
  );
}
